import React, { useState, useEffect } from "react";
import { useApi } from "../../hooks/useApi";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Separator } from "../../components/ui/separator";
import { toast } from "sonner";
import {
    Hash,
    FileText,
    Handshake,
    Scale,
    PenTool,
    Palette,
    Save,
    Loader2,
    Eye,
    EyeOff,
} from "lucide-react";
import { QuotePreview } from "../../components/pdf/PDFPreview";

export const QuoteSettingsPage = () => {
    const { get, put } = useApi();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState(null);
    const [billingSettings, setBillingSettings] = useState(null);
    const [showPreview, setShowPreview] = useState(true);

    useEffect(() => {
        const fetchSettings = async () => {
            const results = await Promise.allSettled([
                get("/settings/quotes"),
                get("/settings/billing"),
            ]);
            if (results[0].status === "fulfilled") setSettings(results[0].value);
            else toast.error("Erreur lors du chargement des paramètres de devis");
            if (results[1].status === "fulfilled") setBillingSettings(results[1].value);
            setLoading(false);
        };
        fetchSettings();
    }, [get]);

    const updateField = (section, field, value) => {
        setSettings(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await put("/settings/quotes", {
                numbering: settings.numbering,
                introduction: settings.introduction,
                commercial_terms: settings.commercial_terms,
                clauses: settings.clauses,
                signature: settings.signature,
                footer: settings.footer,
                branding: settings.branding,
            });
            toast.success("Paramètres de devis enregistrés");
        } catch (error) {
            toast.error("Erreur lors de la sauvegarde");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!settings) return null;

    return (
        <div className="space-y-6 animate-fade-in" data-testid="quote-settings-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">
                        Paramètres des devis
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Personnalisez vos devis avec vos informations commerciales
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={() => setShowPreview(!showPreview)}
                        data-testid="toggle-quote-preview"
                    >
                        {showPreview ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                        {showPreview ? "Masquer l'aperçu" : "Aperçu"}
                    </Button>
                    <Button onClick={handleSave} disabled={saving} data-testid="save-quote-settings">
                        {saving ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <Save className="w-4 h-4 mr-2" />
                        )}
                        {saving ? "Enregistrement..." : "Enregistrer"}
                    </Button>
                </div>
            </div>

            <div className={`grid gap-6 ${showPreview ? "grid-cols-1 xl:grid-cols-[1fr_420px]" : "grid-cols-1"}`}>
                <div>
                    <Tabs defaultValue="numbering" className="space-y-6">
                        <TabsList className="grid grid-cols-3 sm:grid-cols-6 w-full max-w-3xl">
                    <TabsTrigger value="numbering">
                        <Hash className="w-4 h-4 mr-2 hidden sm:inline" />
                        Numérotation
                    </TabsTrigger>
                    <TabsTrigger value="intro">
                        <FileText className="w-4 h-4 mr-2 hidden sm:inline" />
                        Introduction
                    </TabsTrigger>
                    <TabsTrigger value="terms">
                        <Handshake className="w-4 h-4 mr-2 hidden sm:inline" />
                        Conditions
                    </TabsTrigger>
                    <TabsTrigger value="clauses">
                        <Scale className="w-4 h-4 mr-2 hidden sm:inline" />
                        Clauses
                    </TabsTrigger>
                    <TabsTrigger value="signature">
                        <PenTool className="w-4 h-4 mr-2 hidden sm:inline" />
                        Signature
                    </TabsTrigger>
                    <TabsTrigger value="branding">
                        <Palette className="w-4 h-4 mr-2 hidden sm:inline" />
                        Apparence
                    </TabsTrigger>
                </TabsList>

                {/* Numbering Tab */}
                <TabsContent value="numbering">
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading">Numérotation des devis</CardTitle>
                            <CardDescription>Définissez le format de numérotation de vos devis</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label>Préfixe</Label>
                                    <Input
                                        value={settings.numbering?.prefix || "DEV"}
                                        onChange={(e) => updateField("numbering", "prefix", e.target.value)}
                                        placeholder="DEV"
                                        data-testid="quote-prefix-input"
                                    />
                                </div>
                                <div>
                                    <Label>Format</Label>
                                    <Input
                                        value={settings.numbering?.format || "{prefix}-{year}-{number:03d}"}
                                        onChange={(e) => updateField("numbering", "format", e.target.value)}
                                        placeholder="{prefix}-{year}-{number:03d}"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">Exemple: DEV-2026-001</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Introduction Tab */}
                <TabsContent value="intro">
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading">Texte d'introduction</CardTitle>
                            <CardDescription>Texte affiché en haut de chaque devis</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div>
                                <Label>Texte par défaut</Label>
                                <Textarea
                                    value={settings.introduction?.default_text || ""}
                                    onChange={(e) => updateField("introduction", "default_text", e.target.value)}
                                    placeholder="Suite à notre échange, nous avons le plaisir de vous adresser notre proposition..."
                                    rows={4}
                                    data-testid="quote-intro-text"
                                />
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Commercial Terms Tab */}
                <TabsContent value="terms">
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading">Conditions commerciales</CardTitle>
                            <CardDescription>Définissez les conditions par défaut de vos devis</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label>Durée de validité (jours)</Label>
                                    <Input
                                        type="number"
                                        value={settings.commercial_terms?.validity_days || 30}
                                        onChange={(e) => updateField("commercial_terms", "validity_days", parseInt(e.target.value) || 30)}
                                        data-testid="quote-validity-days"
                                    />
                                </div>
                                <div>
                                    <Label>Délai d'exécution</Label>
                                    <Input
                                        value={settings.commercial_terms?.execution_delay || ""}
                                        onChange={(e) => updateField("commercial_terms", "execution_delay", e.target.value)}
                                        placeholder="Ex: 2 à 4 semaines"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label>Conditions de paiement</Label>
                                <Textarea
                                    value={settings.commercial_terms?.payment_terms || ""}
                                    onChange={(e) => updateField("commercial_terms", "payment_terms", e.target.value)}
                                    placeholder="Ex: 50% à la commande, 50% à la livraison"
                                    rows={2}
                                />
                            </div>
                            <Separator />
                            <div className="flex items-center justify-between">
                                <div>
                                    <Label>Exiger un acompte</Label>
                                    <p className="text-sm text-muted-foreground">Demander un acompte à la signature</p>
                                </div>
                                <Switch
                                    checked={settings.commercial_terms?.deposit_required || false}
                                    onCheckedChange={(checked) => updateField("commercial_terms", "deposit_required", checked)}
                                />
                            </div>
                            {settings.commercial_terms?.deposit_required && (
                                <div className="w-48">
                                    <Label>Pourcentage d'acompte (%)</Label>
                                    <Input
                                        type="number"
                                        value={settings.commercial_terms?.deposit_percentage || ""}
                                        onChange={(e) => updateField("commercial_terms", "deposit_percentage", parseFloat(e.target.value) || null)}
                                        placeholder="30"
                                    />
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Clauses Tab */}
                <TabsContent value="clauses">
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading">Clauses et conditions</CardTitle>
                            <CardDescription>Textes juridiques et clauses contractuelles</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div>
                                <Label>Conditions générales</Label>
                                <Textarea
                                    value={settings.clauses?.general_conditions || ""}
                                    onChange={(e) => updateField("clauses", "general_conditions", e.target.value)}
                                    placeholder="Conditions générales de vente..."
                                    rows={4}
                                />
                            </div>
                            <div>
                                <Label>Clauses spécifiques</Label>
                                <Textarea
                                    value={settings.clauses?.specific_clauses || ""}
                                    onChange={(e) => updateField("clauses", "specific_clauses", e.target.value)}
                                    placeholder="Clauses spécifiques au secteur..."
                                    rows={3}
                                />
                            </div>
                            <div>
                                <Label>Engagements</Label>
                                <Textarea
                                    value={settings.clauses?.commitments || ""}
                                    onChange={(e) => updateField("clauses", "commitments", e.target.value)}
                                    placeholder="Nos engagements de qualité..."
                                    rows={3}
                                />
                            </div>
                            <div>
                                <Label>Limitations</Label>
                                <Textarea
                                    value={settings.clauses?.limitations || ""}
                                    onChange={(e) => updateField("clauses", "limitations", e.target.value)}
                                    placeholder="Limitations de responsabilité..."
                                    rows={3}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Signature Tab */}
                <TabsContent value="signature">
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading">Signature</CardTitle>
                            <CardDescription>Informations de signature pour vos devis</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label>Nom du signataire</Label>
                                    <Input
                                        value={settings.signature?.signer_name || ""}
                                        onChange={(e) => updateField("signature", "signer_name", e.target.value)}
                                        placeholder="Nom et prénom"
                                        data-testid="quote-signer-name"
                                    />
                                </div>
                                <div>
                                    <Label>Fonction / Titre</Label>
                                    <Input
                                        value={settings.signature?.signer_title || ""}
                                        onChange={(e) => updateField("signature", "signer_title", e.target.value)}
                                        placeholder="Directeur Général"
                                    />
                                </div>
                                <div>
                                    <Label>URL de l'image de signature</Label>
                                    <Input
                                        value={settings.signature?.signature_image_url || ""}
                                        onChange={(e) => updateField("signature", "signature_image_url", e.target.value)}
                                        placeholder="https://..."
                                    />
                                </div>
                                <div>
                                    <Label>URL du cachet</Label>
                                    <Input
                                        value={settings.signature?.stamp_image_url || ""}
                                        onChange={(e) => updateField("signature", "stamp_image_url", e.target.value)}
                                        placeholder="https://..."
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Branding Tab */}
                <TabsContent value="branding">
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading">Apparence des devis</CardTitle>
                            <CardDescription>Personnalisez les couleurs et le style de vos devis</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label>Couleur principale</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            type="color"
                                            value={settings.branding?.primary_color || "#1E3A8A"}
                                            onChange={(e) => updateField("branding", "primary_color", e.target.value)}
                                            className="w-16 h-10 p-1"
                                        />
                                        <Input
                                            value={settings.branding?.primary_color || "#1E3A8A"}
                                            onChange={(e) => updateField("branding", "primary_color", e.target.value)}
                                            placeholder="#1E3A8A"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label>Couleur secondaire</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            type="color"
                                            value={settings.branding?.secondary_color || "#F97316"}
                                            onChange={(e) => updateField("branding", "secondary_color", e.target.value)}
                                            className="w-16 h-10 p-1"
                                        />
                                        <Input
                                            value={settings.branding?.secondary_color || "#F97316"}
                                            onChange={(e) => updateField("branding", "secondary_color", e.target.value)}
                                            placeholder="#F97316"
                                        />
                                    </div>
                                </div>
                            </div>
                            <Separator />
                            <div className="flex items-center justify-between">
                                <div>
                                    <Label>Afficher le logo</Label>
                                    <p className="text-sm text-muted-foreground">Inclure le logo de l'entreprise sur les devis</p>
                                </div>
                                <Switch
                                    checked={settings.branding?.show_logo ?? true}
                                    onCheckedChange={(checked) => updateField("branding", "show_logo", checked)}
                                />
                            </div>
                            <Separator />
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <Label>Afficher le nom de l'entreprise</Label>
                                        <p className="text-sm text-muted-foreground">Nom de l'entreprise dans le pied de page</p>
                                    </div>
                                    <Switch
                                        checked={settings.footer?.show_orkestria_mention ?? true}
                                        onCheckedChange={(checked) => updateField("footer", "show_orkestria_mention", checked)}
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
                </div>

                {/* Live Preview Panel */}
                {showPreview && (
                    <div className="hidden xl:block sticky top-6 self-start" data-testid="quote-preview-panel">
                        <QuotePreview billingSettings={billingSettings} quoteSettings={settings} />
                    </div>
                )}
            </div>
        </div>
    );
};
